import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import {
  FactCheck as ValidationIcon,
  People as StudentIcon,
  DirectionsBus as TransportIcon,
  Payments as FeePaymentIcon,
  ReceiptLong as TransactionIcon,
  EventAvailable as AttendanceIcon,
  Grade as ExamMarksIcon,
  Class as ClassSubjectIcon,
  ContentCopy as DuplicateIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  FilterAlt as FilterIcon,
  InfoOutlined as DetailsIcon,
  AutoFixHigh as FixIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import PageContentLayout from "../components/layout/PageContentLayout";
import { crmColors } from "../theme/colors";
import apiService from "../services/apiService";

const BRAND = crmColors.primary.main;

const getSchoolStatusMeta = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "active") {
    return { label: "Active", color: "success", isActive: true };
  }
  if (normalized === "inactive" || normalized === "in active") {
    return { label: "Inactive", color: "default", isActive: false };
  }
  if (normalized === "delisted") {
    return { label: "Delisted", color: "error", isActive: false };
  }
  return {
    label: status?.trim() || "Unknown",
    color: "warning",
    isActive: false,
  };
};

const SchoolStatusChip = ({ status, size = "small" }) => {
  const meta = getSchoolStatusMeta(status);
  return (
    <Chip
      size={size}
      label={meta.label}
      color={meta.color}
      variant={meta.isActive ? "filled" : "outlined"}
      sx={{ fontWeight: 700 }}
    />
  );
};

const DUPLICATE_OPTIONS = [
  {
    id: "student-data",
    label: "Student Data",
    description: "Find and clean duplicate student records",
    icon: <StudentIcon sx={{ fontSize: 22 }} />,
    color: BRAND,
  },
  {
    id: "transport",
    label: "Transport",
    description: "Duplicate transport assignments & routes",
    icon: <TransportIcon sx={{ fontSize: 22 }} />,
    color: crmColors.info.main,
  },
  {
    id: "fee-payment",
    label: "Fee Payment",
    description: "Duplicate fee payment lines (tm_fee_payment)",
    icon: <FeePaymentIcon sx={{ fontSize: 22 }} />,
    color: crmColors.warning.main,
  },
  {
    id: "transaction-duplicate",
    label: "Transaction Duplicate",
    description: "Duplicate receipts in tm_transaction_master",
    icon: <TransactionIcon sx={{ fontSize: 22 }} />,
    color: "#b45309",
  },
  {
    id: "attendance",
    label: "Attendance",
    description: "Duplicate attendance & report entries",
    icon: <AttendanceIcon sx={{ fontSize: 22 }} />,
    color: crmColors.success.main,
  },
  {
    id: "exam-marks",
    label: "Exam Marks",
    description: "Duplicate student mark entries",
    icon: <ExamMarksIcon sx={{ fontSize: 22 }} />,
    color: crmColors.error.main,
  },
  {
    id: "class-subject",
    label: "Class Subject",
    description: "Duplicate class–subject mappings",
    icon: <ClassSubjectIcon sx={{ fontSize: 22 }} />,
    color: crmColors.secondary.main,
  },
];

const OptionTile = ({ option, selected, onSelect }) => (
  <Paper
    elevation={0}
    onClick={() => onSelect(option.id)}
    sx={{
      height: "100%",
      px: 1.75,
      py: 1.75,
      borderRadius: 2.5,
      cursor: "pointer",
      border: `1.5px solid ${selected ? option.color : alpha(option.color, 0.22)}`,
      background: selected
        ? `linear-gradient(145deg, ${alpha(option.color, 0.16)} 0%, ${alpha(option.color, 0.04)} 50%, #fff 100%)`
        : `linear-gradient(145deg, ${alpha(option.color, 0.08)} 0%, #fff 100%)`,
      transition: "transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: option.color,
        boxShadow: `0 8px 18px ${alpha(option.color, 0.12)}`,
      },
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: alpha(option.color, 0.14),
          color: option.color,
        }}
      >
        {option.icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: crmColors.text.primary }}>
            {option.label}
          </Typography>
          <ChevronRightIcon
            sx={{
              fontSize: 18,
              color: selected ? option.color : crmColors.text.disabled,
            }}
          />
        </Stack>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.35,
            lineHeight: 1.35,
            color: crmColors.text.secondary,
          }}
        >
          {option.description}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const Validation = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [studentDuplicates, setStudentDuplicates] = useState([]);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [fixingRowKey, setFixingRowKey] = useState("");
  const [studentError, setStudentError] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [appliedSchoolCode, setAppliedSchoolCode] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsData, setDetailsData] = useState(null);
  const [detailsRow, setDetailsRow] = useState(null);
  const [detailsType, setDetailsType] = useState("student");
  const [transportDuplicates, setTransportDuplicates] = useState([]);
  const [loadingTransport, setLoadingTransport] = useState(false);
  const [transportError, setTransportError] = useState("");
  const [feeDuplicates, setFeeDuplicates] = useState([]);
  const [loadingFee, setLoadingFee] = useState(false);
  const [feeError, setFeeError] = useState("");
  const [txnDuplicates, setTxnDuplicates] = useState([]);
  const [loadingTxn, setLoadingTxn] = useState(false);
  const [txnError, setTxnError] = useState("");

  const active = DUPLICATE_OPTIONS.find((o) => o.id === selectedOption);

  const applySchoolFilter = (code = schoolFilter) => {
    setAppliedSchoolCode(String(code || "").trim());
  };

  const loadStudentDuplicates = useCallback(async () => {
    setLoadingStudent(true);
    setStudentError("");
    setStudentDuplicates([]);

    try {
      const result = await apiService.getDuplicateStudentEnrollments({
        school_code: appliedSchoolCode,
      });

      if (result.success) {
        setStudentDuplicates(Array.isArray(result.data) ? result.data : []);
      } else {
        setStudentDuplicates([]);
        setStudentError(result.message || result.error || "Failed to load duplicates");
      }
    } catch (err) {
      setStudentDuplicates([]);
      setStudentError(err?.message || "Failed to load duplicates");
    } finally {
      setLoadingStudent(false);
    }
  }, [appliedSchoolCode]);

  const getRowKey = (row) => `${row.school_code}|${row.student_enrollment_no}`;

  const getTransportRowKey = (row) => `${row.school_code}|${row.student_id}|${row.session_id}`;

  const getFeeRowKey = (row) =>
    `${row.school_code}|${row.session_id}|${row.student_id}|${row.class_id}|${row.section_id}|${row.fee_id}|${row.year_month}|${row.fee_payable}|${row.fee_paid}`;

  const getTxnRowKey = (row) =>
    `${row.school_code}|${row.session_id}|${row.student_id}|${row.class_id}|${row.section_id}|${row.paid_amount}|${row.transaction_date}|${row.transaction_for || "FEE"}`;

  const loadTransportDuplicates = useCallback(async () => {
    setLoadingTransport(true);
    setTransportError("");
    setTransportDuplicates([]);

    try {
      const result = await apiService.getDuplicateTransport({
        school_code: appliedSchoolCode,
      });

      if (result.success) {
        setTransportDuplicates(Array.isArray(result.data) ? result.data : []);
      } else {
        setTransportDuplicates([]);
        setTransportError(result.message || result.error || "Failed to load duplicates");
      }
    } catch (err) {
      setTransportDuplicates([]);
      setTransportError(err?.message || "Failed to load duplicates");
    } finally {
      setLoadingTransport(false);
    }
  }, [appliedSchoolCode]);

  const openDuplicateDetails = useCallback(async (row) => {
    setDetailsOpen(true);
    setDetailsRow(row);
    setDetailsType("student");
    setDetailsLoading(true);
    setDetailsError("");
    setDetailsData(null);
    try {
      const result = await apiService.getDuplicateStudentEnrollmentDetails({
        school_code: row.school_code,
        student_enrollment_no: row.student_enrollment_no,
      });
      if (result.success && result.data) {
        setDetailsData(result.data);
      } else {
        setDetailsError(result.message || result.error || "Failed to load details");
      }
    } catch (err) {
      setDetailsError(err?.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const openTransportDuplicateDetails = useCallback(async (row) => {
    setDetailsOpen(true);
    setDetailsRow(row);
    setDetailsType("transport");
    setDetailsLoading(true);
    setDetailsError("");
    setDetailsData(null);
    try {
      const result = await apiService.getDuplicateTransportDetails({
        school_code: row.school_code,
        student_id: row.student_id,
        session_id: row.session_id,
      });
      if (result.success && result.data) {
        setDetailsData(result.data);
      } else {
        setDetailsError(result.message || result.error || "Failed to load details");
      }
    } catch (err) {
      setDetailsError(err?.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const loadFeeDuplicates = useCallback(async () => {
    setLoadingFee(true);
    setFeeError("");
    setFeeDuplicates([]);

    try {
      const result = await apiService.getDuplicateFeeTransactions({
        school_code: appliedSchoolCode,
      });

      if (result.success) {
        setFeeDuplicates(Array.isArray(result.data) ? result.data : []);
      } else {
        setFeeDuplicates([]);
        setFeeError(result.message || result.error || "Failed to load duplicates");
      }
    } catch (err) {
      setFeeDuplicates([]);
      setFeeError(err?.message || "Failed to load duplicates");
    } finally {
      setLoadingFee(false);
    }
  }, [appliedSchoolCode]);

  const openFeeDuplicateDetails = useCallback(async (row) => {
    setDetailsOpen(true);
    setDetailsRow(row);
    setDetailsType("fee-payment");
    setDetailsLoading(true);
    setDetailsError("");
    setDetailsData(null);
    try {
      const result = await apiService.getDuplicateFeeTransactionDetails({
        school_code: row.school_code,
        session_id: row.session_id,
        student_id: row.student_id,
        class_id: row.class_id,
        section_id: row.section_id,
        fee_id: row.fee_id,
        year_month: row.year_month,
        fee_payable: row.fee_payable,
        fee_paid: row.fee_paid,
      });
      if (result.success && result.data) {
        setDetailsData(result.data);
      } else {
        setDetailsError(result.message || result.error || "Failed to load details");
      }
    } catch (err) {
      setDetailsError(err?.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const closeDuplicateDetails = useCallback(() => {
    setDetailsOpen(false);
    setDetailsRow(null);
    setDetailsData(null);
    setDetailsError("");
    setDetailsType("student");
  }, []);

  const fixRowDuplicate = useCallback(
    async (row) => {
      const confirmed = window.confirm(
        `Fix duplicate enrollment ${row.student_enrollment_no} for school ${row.school_code}?\n\n` +
          "Same-name ghost admission rows will be removed.\n" +
          "Different students sharing one enrollment number will get new unique numbers."
      );
      if (!confirmed) return;

      const rowKey = getRowKey(row);
      setFixingRowKey(rowKey);
      setStudentError("");
      try {
        const result = await apiService.fixDuplicateStudentEnrollments({
          school_code: row.school_code,
          student_enrollment_no: row.student_enrollment_no,
          dry_run: false,
        });
        if (result.success) {
          await loadStudentDuplicates();
          if (detailsOpen && detailsRow && getRowKey(detailsRow) === rowKey) {
            closeDuplicateDetails();
          }
        } else {
          setStudentError(result.message || result.error || "Failed to fix duplicate");
        }
      } catch (err) {
        setStudentError(err?.message || "Failed to fix duplicate");
      } finally {
        setFixingRowKey("");
      }
    },
    [closeDuplicateDetails, detailsOpen, detailsRow, loadStudentDuplicates]
  );

  const fixTransportRowDuplicate = useCallback(
    async (row) => {
      const confirmed = window.confirm(
        `Fix duplicate transport for ${row.student_id} (${row.session_id}) in school ${row.school_code}?\n\n` +
          "Extra rows in tm_transport_student, tm_student_services, and tm_student_services_fee will be removed.\n" +
          "The newest active row in each group is kept."
      );
      if (!confirmed) return;

      const rowKey = getTransportRowKey(row);
      setFixingRowKey(rowKey);
      setTransportError("");
      try {
        const result = await apiService.fixDuplicateTransport({
          school_code: row.school_code,
          student_id: row.student_id,
          session_id: row.session_id,
          dry_run: false,
        });
        if (result.success) {
          await loadTransportDuplicates();
          if (detailsOpen && detailsRow && getTransportRowKey(detailsRow) === rowKey) {
            closeDuplicateDetails();
          }
        } else {
          setTransportError(result.message || result.error || "Failed to fix duplicate");
        }
      } catch (err) {
        setTransportError(err?.message || "Failed to fix duplicate");
      } finally {
        setFixingRowKey("");
      }
    },
    [closeDuplicateDetails, detailsOpen, detailsRow, loadTransportDuplicates]
  );

  const fixFeeRowDuplicate = useCallback(
    async (row) => {
      const payload = {
        school_code: String(row.school_code ?? ""),
        session_id: String(row.session_id ?? ""),
        student_id: String(row.student_id ?? ""),
        class_id: String(row.class_id ?? ""),
        section_id: String(row.section_id ?? ""),
        fee_id: String(row.fee_id ?? ""),
        year_month: String(row.year_month ?? ""),
        fee_payable: String(row.fee_payable ?? ""),
        fee_paid: String(row.fee_paid ?? ""),
        match_type: row.match_type,
      };

      const matchLabel =
        payload.match_type === "identical"
          ? "Identical (fully paid)"
          : payload.match_type === "likely_double_submit"
            ? "Likely double-submit (partial)"
            : payload.match_type || "duplicate";
      const confirmed = window.confirm(
        `Fix duplicate fee payment for ${payload.student_id} in school ${payload.school_code}?\n\n` +
          `Fee ${payload.fee_id} · Month ${payload.year_month}\n` +
          `Payable ${payload.fee_payable} · Paid ${payload.fee_paid}\n` +
          `Match: ${matchLabel}\n\n` +
          "Oldest payment/transaction is kept. Duplicate transactions are fully cleared from fee_payment, fine_student, old_due, accessories, event payment, and transaction_master."
      );
      if (!confirmed) return;

      const rowKey = getFeeRowKey(payload);
      setFixingRowKey(rowKey);
      setFeeError("");
      if (detailsOpen && detailsType === "fee-payment") {
        setDetailsError("");
      }
      try {
        const result = await apiService.fixDuplicateFeeTransactions({
          ...payload,
          dry_run: false,
          force: true,
        });
        const data = result.data || {};
        const removed = Number(data.payment_rows_removed || 0);
        const fixedGroups = Number(data.groups_fixed || 0);
        if (result.success && (removed > 0 || fixedGroups > 0)) {
          await loadFeeDuplicates();
          // Always close details dialog after a successful fee fix from either list or dialog.
          if (detailsOpen && detailsType === "fee-payment") {
            closeDuplicateDetails();
          }
        } else {
          const detailMsg =
            data.remark ||
            (Array.isArray(data.errors) && data.errors[0]) ||
            data.skipped?.[0]?.actions?.[0] ||
            data.skipped?.[0]?.errors?.[0] ||
            result.message ||
            result.error ||
            "Fix did not remove any duplicate fee payment rows";
          setFeeError(detailMsg);
          if (detailsOpen && detailsType === "fee-payment") {
            setDetailsError(detailMsg);
          }
        }
      } catch (err) {
        const msg = err?.message || "Failed to fix duplicate fee payment";
        setFeeError(msg);
        if (detailsOpen && detailsType === "fee-payment") {
          setDetailsError(msg);
        }
      } finally {
        setFixingRowKey("");
      }
    },
    [closeDuplicateDetails, detailsOpen, detailsType, loadFeeDuplicates]
  );

  const loadTxnDuplicates = useCallback(async () => {
    setLoadingTxn(true);
    setTxnError("");
    setTxnDuplicates([]);
    try {
      const result = await apiService.getDuplicateMasterTransactions({
        school_code: appliedSchoolCode,
      });
      if (result.success) {
        setTxnDuplicates(Array.isArray(result.data) ? result.data : []);
      } else {
        setTxnError(result.message || result.error || "Failed to load duplicates");
      }
    } catch (err) {
      setTxnError(err?.message || "Failed to load duplicates");
    } finally {
      setLoadingTxn(false);
    }
  }, [appliedSchoolCode]);

  const openTxnDuplicateDetails = useCallback(async (row) => {
    setDetailsOpen(true);
    setDetailsRow(row);
    setDetailsType("transaction-duplicate");
    setDetailsLoading(true);
    setDetailsError("");
    setDetailsData(null);
    try {
      const result = await apiService.getDuplicateMasterTransactionDetails({
        school_code: row.school_code,
        session_id: row.session_id,
        student_id: row.student_id,
        class_id: row.class_id,
        section_id: row.section_id,
        paid_amount: row.paid_amount,
        transaction_date: row.transaction_date,
        transaction_for: row.transaction_for || "FEE",
      });
      if (result.success && result.data) {
        setDetailsData(result.data);
      } else {
        setDetailsError(result.message || result.error || "Failed to load details");
      }
    } catch (err) {
      setDetailsError(err?.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const fixTxnRowDuplicate = useCallback(
    async (row) => {
      const confirmed = window.confirm(
        `Fix duplicate transaction for ${row.student_id} in school ${row.school_code}?\n\n` +
          `Amount ${row.paid_amount} · Date ${row.transaction_date} · For ${row.transaction_for || "FEE"}\n` +
          `Class ${row.class_id}/${row.section_id} · Session ${row.session_id}\n\n` +
          "Oldest transaction is kept. Extra receipts are cleared from tm_fee_payment, tm_fine_student, and tm_transaction_master."
      );
      if (!confirmed) return;

      const rowKey = getTxnRowKey(row);
      setFixingRowKey(rowKey);
      setTxnError("");
      if (detailsOpen && detailsType === "transaction-duplicate") {
        setDetailsError("");
      }
      try {
        const result = await apiService.fixDuplicateMasterTransactions({
          school_code: String(row.school_code ?? ""),
          session_id: String(row.session_id ?? ""),
          student_id: String(row.student_id ?? ""),
          class_id: String(row.class_id ?? ""),
          section_id: String(row.section_id ?? ""),
          paid_amount: String(row.paid_amount ?? ""),
          transaction_date: String(row.transaction_date ?? ""),
          transaction_for: String(row.transaction_for || "FEE"),
          dry_run: false,
          force: true,
        });
        const data = result.data || {};
        const removed = Number(data.transactions_removed || 0);
        const fixedGroups = Number(data.groups_fixed || 0);
        if (result.success && (removed > 0 || fixedGroups > 0)) {
          await loadTxnDuplicates();
          if (detailsOpen && detailsType === "transaction-duplicate") {
            closeDuplicateDetails();
          }
        } else {
          const detailMsg =
            data.remark ||
            (Array.isArray(data.errors) && data.errors[0]) ||
            data.skipped?.[0]?.actions?.[0] ||
            result.message ||
            result.error ||
            "Fix did not remove any duplicate transactions";
          setTxnError(detailMsg);
          if (detailsOpen && detailsType === "transaction-duplicate") {
            setDetailsError(detailMsg);
          }
        }
      } catch (err) {
        const msg = err?.message || "Failed to fix duplicate transaction";
        setTxnError(msg);
        if (detailsOpen && detailsType === "transaction-duplicate") {
          setDetailsError(msg);
        }
      } finally {
        setFixingRowKey("");
      }
    },
    [closeDuplicateDetails, detailsOpen, detailsType, loadTxnDuplicates]
  );

  useEffect(() => {
    if (selectedOption === "student-data") {
      loadStudentDuplicates();
    }
    if (selectedOption === "transport") {
      loadTransportDuplicates();
    }
    if (selectedOption === "fee-payment") {
      loadFeeDuplicates();
    }
    if (selectedOption === "transaction-duplicate") {
      loadTxnDuplicates();
    }
  }, [
    selectedOption,
    appliedSchoolCode,
    loadStudentDuplicates,
    loadTransportDuplicates,
    loadFeeDuplicates,
    loadTxnDuplicates,
  ]);

  const handleSelect = (id) => {
    setSelectedOption(id);
    if (
      id === "student-data" ||
      id === "transport" ||
      id === "fee-payment" ||
      id === "transaction-duplicate"
    ) {
      setSchoolFilter("");
      setAppliedSchoolCode("");
    }
    if (id !== "student-data") {
      setStudentError("");
    }
    if (id !== "transport") {
      setTransportError("");
    }
    if (id !== "fee-payment") {
      setFeeError("");
    }
    if (id !== "transaction-duplicate") {
      setTxnError("");
    }
    if (
      id !== "student-data" &&
      id !== "transport" &&
      id !== "fee-payment" &&
      id !== "transaction-duplicate"
    ) {
      closeDuplicateDetails();
    }
  };

  return (
    <PageContentLayout
      title="Validation"
      subtitle="Review and validate school records"
      darkBar
      contentPadding={2}
      breadcrumbs={[
        {
          label: "Validation",
          icon: <ValidationIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      showBack={false}
    >
      <Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${alpha(BRAND, 0.18)}`,
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              px: 1.75,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderBottom: `1px solid ${alpha(BRAND, 0.12)}`,
              bgcolor: alpha(BRAND, 0.06),
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(BRAND, 0.14),
                color: crmColors.primary.dark,
                flexShrink: 0,
              }}
            >
              <DuplicateIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Manage Duplicate Records
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                Choose a record type to find and remove duplicates
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 1.75 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1.5,
              }}
            >
              {DUPLICATE_OPTIONS.map((option) => (
                <OptionTile
                  key={option.id}
                  option={option}
                  selected={selectedOption === option.id}
                  onSelect={handleSelect}
                />
              ))}
            </Box>

            {active && selectedOption === "student-data" && (
              <Box
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(active.color, 0.22)}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 1.75,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                    bgcolor: alpha(active.color, 0.06),
                    borderBottom: `1px solid ${alpha(active.color, 0.12)}`,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: active.color }}>
                      Duplicate Enrollment Numbers
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Open Details for student names and class info
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <TextField
                      size="small"
                      label="School code"
                          placeholder="All schools"
                      value={schoolFilter}
                      onChange={(event) => setSchoolFilter(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          applySchoolFilter(event.currentTarget.value);
                        }
                      }}
                      disabled={loadingStudent || !!fixingRowKey}
                      sx={{ minWidth: 200, maxWidth: 260 }}
                          InputProps={{
                            startAdornment: (
                          <FilterIcon sx={{ fontSize: 18, color: active.color, mr: 0.75 }} />
                            ),
                          }}
                        />
                      <Button
                        size="small"
                      variant="contained"
                      onClick={() => applySchoolFilter()}
                      disabled={loadingStudent || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        bgcolor: active.color,
                        "&:hover": { bgcolor: crmColors.primary.dark },
                      }}
                    >
                      Apply
                      </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSchoolFilter("");
                        setAppliedSchoolCode("");
                      }}
                      disabled={loadingStudent || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        borderColor: alpha(active.color, 0.4),
                        color: active.color,
                      }}
                    >
                      All schools
                    </Button>
                    {!loadingStudent && !studentError && (
                      <Chip
                        size="small"
                        label={`${studentDuplicates.length} duplicate${studentDuplicates.length === 1 ? "" : "s"}`}
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(active.color, 0.12),
                          color: active.color,
                        }}
                      />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={loadingStudent ? <CircularProgress size={14} /> : <RefreshIcon />}
                      onClick={() => loadStudentDuplicates()}
                      disabled={loadingStudent || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        borderColor: alpha(active.color, 0.4),
                        color: active.color,
                        "&:hover": {
                          borderColor: active.color,
                          bgcolor: alpha(active.color, 0.06),
                        },
                      }}
                    >
                      Refresh
                    </Button>
                  </Stack>
                </Box>

                {loadingStudent && studentDuplicates.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress size={28} sx={{ color: active.color }} />
                  </Box>
                ) : studentError && studentDuplicates.length === 0 ? (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="error">
                      {studentError}
                    </Typography>
                  </Box>
                ) : studentDuplicates.length === 0 ? (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {appliedSchoolCode
                        ? `No duplicate enrollment numbers found for school code ${appliedSchoolCode}.`
                        : "No duplicate enrollment numbers found."}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {studentError ? (
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderBottom: `1px solid ${alpha(crmColors.warning.main, 0.2)}`,
                          bgcolor: alpha(crmColors.warning.main, 0.08),
                        }}
                      >
                        <Typography variant="body2" color="error">
                          {studentError}
                        </Typography>
                      </Box>
                    ) : null}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 56 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 120 }}>
                            School Code
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 140 }}>
                            Enrollment No
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 90 }}>
                            Total
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 180 }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {studentDuplicates.map((row, index) => {
                          const rowKey = getRowKey(row);
                          const isFixing = fixingRowKey === rowKey;
                          return (
                            <TableRow
                              key={rowKey}
                              hover
                              sx={{ "&:last-child td": { borderBottom: 0 }, verticalAlign: "middle" }}
                            >
                              <TableCell sx={{ color: "text.secondary" }}>{index + 1}</TableCell>
                              <TableCell>
                                      <Chip
                                        size="small"
                                  label={row.school_code || "—"}
                                        sx={{
                                          fontWeight: 700,
                                          bgcolor: alpha(crmColors.primary.main, 0.1),
                                          color: crmColors.primary.dark,
                                        }}
                                      />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {row.student_enrollment_no || (
                                  <Typography component="span" variant="body2" color="text.disabled">
                                    (empty)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  size="small"
                                  label={row.total}
                                  sx={{
                                    fontWeight: 700,
                                    minWidth: 40,
                                    bgcolor: alpha(crmColors.error.main, 0.1),
                                    color: crmColors.error.main,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DetailsIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => openDuplicateDetails(row)}
                                    disabled={isFixing}
                                    sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}
                                  >
                                    Details
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={
                                      isFixing ? (
                                        <CircularProgress size={14} color="inherit" />
                                      ) : (
                                        <FixIcon sx={{ fontSize: 16 }} />
                                      )
                                    }
                                    onClick={() => fixRowDuplicate(row)}
                                    disabled={isFixing || !!fixingRowKey}
                                    sx={{
                                      textTransform: "none",
                                      minWidth: 0,
                                      px: 1.25,
                                      bgcolor: active.color,
                                      "&:hover": { bgcolor: crmColors.primary.dark },
                                    }}
                                  >
                                    Fix
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </>
                )}
              </Box>
            )}

            {active && selectedOption === "transport" && (
              <Box
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(active.color, 0.22)}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 1.75,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                    bgcolor: alpha(active.color, 0.06),
                    borderBottom: `1px solid ${alpha(active.color, 0.12)}`,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: active.color }}>
                      Duplicate Transport Assignments
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Checks tm_transport_student, tm_student_services, tm_student_services_fee
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField
                      size="small"
                      label="School code"
                      placeholder="All schools"
                      value={schoolFilter}
                      onChange={(event) => setSchoolFilter(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          applySchoolFilter(event.currentTarget.value);
                        }
                      }}
                      disabled={loadingTransport || !!fixingRowKey}
                      sx={{ minWidth: 200, maxWidth: 260 }}
                      InputProps={{
                        startAdornment: (
                          <FilterIcon sx={{ fontSize: 18, color: active.color, mr: 0.75 }} />
                        ),
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => applySchoolFilter()}
                      disabled={loadingTransport || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        bgcolor: active.color,
                        "&:hover": { bgcolor: crmColors.primary.dark },
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSchoolFilter("");
                        setAppliedSchoolCode("");
                      }}
                      disabled={loadingTransport || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        borderColor: alpha(active.color, 0.4),
                        color: active.color,
                      }}
                    >
                      All schools
                    </Button>
                    {!loadingTransport && !transportError && (
                                      <Chip
                                        size="small"
                        label={`${transportDuplicates.length} duplicate${transportDuplicates.length === 1 ? "" : "s"}`}
                                        sx={{
                                          fontWeight: 600,
                          bgcolor: alpha(active.color, 0.12),
                          color: active.color,
                        }}
                      />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={loadingTransport ? <CircularProgress size={14} /> : <RefreshIcon />}
                      onClick={() => loadTransportDuplicates()}
                      disabled={loadingTransport || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        borderColor: alpha(active.color, 0.4),
                        color: active.color,
                        "&:hover": {
                          borderColor: active.color,
                          bgcolor: alpha(active.color, 0.06),
                        },
                      }}
                    >
                      Refresh
                    </Button>
                  </Stack>
                </Box>

                {loadingTransport && transportDuplicates.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress size={28} sx={{ color: active.color }} />
                  </Box>
                ) : transportError && transportDuplicates.length === 0 ? (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="error">
                      {transportError}
                                  </Typography>
                  </Box>
                ) : transportDuplicates.length === 0 ? (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {appliedSchoolCode
                        ? `No duplicate transport records found for school code ${appliedSchoolCode}.`
                        : "No duplicate transport records found."}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {transportError ? (
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderBottom: `1px solid ${alpha(crmColors.warning.main, 0.2)}`,
                          bgcolor: alpha(crmColors.warning.main, 0.08),
                        }}
                      >
                        <Typography variant="body2" color="error">
                          {transportError}
                        </Typography>
                      </Box>
                    ) : null}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 56 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 100 }}>
                              School
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 120 }}>
                              Student
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 100 }}>
                              Session
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 70 }}>
                              Trans.
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 70 }}>
                              Svc.
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 70 }}>
                              Fee
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 180 }}>
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transportDuplicates.map((row, index) => {
                            const rowKey = getTransportRowKey(row);
                            const isFixing = fixingRowKey === rowKey;
                            return (
                              <TableRow
                                key={rowKey}
                                hover
                                sx={{ "&:last-child td": { borderBottom: 0 }, verticalAlign: "middle" }}
                              >
                                <TableCell sx={{ color: "text.secondary" }}>{index + 1}</TableCell>
                                <TableCell>
                                      <Chip
                                        size="small"
                                    label={row.school_code || "—"}
                                        sx={{
                                      fontWeight: 700,
                                          bgcolor: alpha(crmColors.info.main, 0.1),
                                          color: crmColors.info.dark,
                                        }}
                                      />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {row.student_id || "—"}
                                  </Typography>
                                  {row.student_name ? (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {row.student_name}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{row.session_id || "—"}</TableCell>
                                <TableCell align="right">
                                  {row.transport_rows > 1 ? (
                                    <Chip
                                      size="small"
                                      label={row.transport_rows}
                                      sx={{
                                        fontWeight: 700,
                                        bgcolor: alpha(crmColors.error.main, 0.1),
                                        color: crmColors.error.main,
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {row.service_rows > 1 ? (
                                    <Chip
                                      size="small"
                                      label={row.service_rows}
                                      sx={{
                                        fontWeight: 700,
                                        bgcolor: alpha(crmColors.error.main, 0.1),
                                        color: crmColors.error.main,
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {row.fee_duplicate_months > 0 ? (
                                    <Chip
                                      size="small"
                                      label={row.fee_duplicate_months}
                                      sx={{
                                        fontWeight: 700,
                                        bgcolor: alpha(crmColors.error.main, 0.1),
                                        color: crmColors.error.main,
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<DetailsIcon sx={{ fontSize: 16 }} />}
                                      onClick={() => openTransportDuplicateDetails(row)}
                                      disabled={isFixing}
                                      sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}
                                    >
                                      Details
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={
                                        isFixing ? (
                                          <CircularProgress size={14} color="inherit" />
                                        ) : (
                                          <FixIcon sx={{ fontSize: 16 }} />
                                        )
                                      }
                                      onClick={() => fixTransportRowDuplicate(row)}
                                      disabled={isFixing || !!fixingRowKey}
                                      sx={{
                                        textTransform: "none",
                                        minWidth: 0,
                                        px: 1.25,
                                        bgcolor: active.color,
                                        "&:hover": { bgcolor: crmColors.primary.dark },
                                      }}
                                    >
                                      Fix
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}

            {active && selectedOption === "fee-payment" && (
              <Box
                sx={{
                  mt: 2.5,
                  border: `1px solid ${alpha(active.color, 0.2)}`,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: alpha(active.color, 0.06),
                    borderBottom: `1px solid ${alpha(active.color, 0.12)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: crmColors.text.primary }}>
                      Duplicate Fee Payments
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fully paid lines (paid ≥ payable) with copies → Identical. Partial equal amounts within 2 minutes → Likely double-submit. Spaced partials (e.g. 500 then later 500 on payable 2500) are not listed.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField
                      size="small"
                      label="School code"
                      placeholder="All schools"
                      value={schoolFilter}
                      onChange={(event) => setSchoolFilter(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          applySchoolFilter(event.currentTarget.value);
                        }
                      }}
                      disabled={loadingFee || !!fixingRowKey}
                      sx={{ minWidth: 200, maxWidth: 260 }}
                      InputProps={{
                        startAdornment: (
                          <FilterIcon sx={{ fontSize: 18, color: active.color, mr: 0.75 }} />
                        ),
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => applySchoolFilter()}
                      disabled={loadingFee || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        bgcolor: active.color,
                        "&:hover": { bgcolor: crmColors.primary.dark },
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSchoolFilter("");
                        setAppliedSchoolCode("");
                      }}
                      disabled={loadingFee || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        borderColor: alpha(active.color, 0.4),
                        color: active.color,
                      }}
                    >
                      All schools
                    </Button>
                    {!loadingFee && !feeError && (
                      <Chip
                        size="small"
                        label={`${feeDuplicates.length} duplicate${feeDuplicates.length === 1 ? "" : "s"}`}
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(active.color, 0.12),
                          color: active.color,
                        }}
                      />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={loadingFee ? <CircularProgress size={14} /> : <RefreshIcon />}
                      onClick={() => loadFeeDuplicates()}
                      disabled={loadingFee || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        borderColor: alpha(active.color, 0.4),
                        color: active.color,
                        "&:hover": {
                          borderColor: active.color,
                          bgcolor: alpha(active.color, 0.06),
                        },
                      }}
                    >
                      Refresh
                    </Button>
                  </Stack>
                </Box>

                {loadingFee && feeDuplicates.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress size={28} sx={{ color: active.color }} />
                  </Box>
                ) : feeError && feeDuplicates.length === 0 ? (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="error">
                      {feeError}
                    </Typography>
                  </Box>
                ) : feeDuplicates.length === 0 ? (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {appliedSchoolCode
                        ? `No duplicate fee payment rows found for school code ${appliedSchoolCode}.`
                        : "No duplicate fee payment rows found."}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {feeError ? (
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderBottom: `1px solid ${alpha(crmColors.warning.main, 0.2)}`,
                          bgcolor: alpha(crmColors.warning.main, 0.08),
                        }}
                      >
                        <Typography variant="body2" color="error">
                          {feeError}
                        </Typography>
                      </Box>
                    ) : null}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 56 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 90 }}>
                              School
                              </TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 110 }}>
                              Student
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 90 }}>
                              Fee
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 90 }}>
                              Month
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 80 }}>
                              Payable
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 80 }}>
                              Paid
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 70 }}>
                              Rows
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", minWidth: 140 }}>
                              Match
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 220 }}>
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {feeDuplicates.map((row, index) => {
                            const rowKey = getFeeRowKey(row);
                            const isIdentical = row.match_type === "identical";
                            const isFixing = fixingRowKey === rowKey;
                            return (
                              <TableRow
                                key={rowKey}
                                hover
                                sx={{ "&:last-child td": { borderBottom: 0 }, verticalAlign: "middle" }}
                              >
                                <TableCell sx={{ color: "text.secondary" }}>{index + 1}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={row.school_code || "—"}
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: alpha(crmColors.warning.main, 0.12),
                                      color: crmColors.warning.dark,
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{row.student_id || "—"}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{row.fee_id || "—"}</TableCell>
                                <TableCell>{row.year_month || "—"}</TableCell>
                                <TableCell align="right">{row.fee_payable || "—"}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                  {row.fee_paid || "—"}
                                </TableCell>
                                <TableCell align="right">
                                <Chip
                                  size="small"
                                  label={row.total}
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(crmColors.error.main, 0.1),
                                    color: crmColors.error.main,
                                  }}
                                />
                              </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={isIdentical ? "Identical" : "Likely double-submit"}
                                    color={isIdentical ? "error" : "warning"}
                                    sx={{ fontWeight: 700 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<DetailsIcon sx={{ fontSize: 16 }} />}
                                      onClick={() => openFeeDuplicateDetails(row)}
                                      disabled={isFixing}
                                      sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}
                                    >
                                      Details
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={
                                        isFixing ? (
                                          <CircularProgress size={14} color="inherit" />
                                        ) : (
                                          <FixIcon sx={{ fontSize: 16 }} />
                                        )
                                      }
                                      onClick={() => fixFeeRowDuplicate(row)}
                                      disabled={isFixing || !!fixingRowKey || row.can_fix === false}
                                      sx={{
                                        textTransform: "none",
                                        minWidth: 0,
                                        px: 1.25,
                                        bgcolor: active.color,
                                        "&:hover": { bgcolor: crmColors.primary.dark },
                                      }}
                                    >
                                      Fix
                                    </Button>
                                  </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </>
                )}
              </Box>
            )}

            {active && selectedOption === "transaction-duplicate" && (
              <Box
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(active.color, 0.22)}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${alpha(active.color, 0.18)}`,
                    bgcolor: alpha(active.color, 0.06),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: crmColors.text.primary }}>
                      Duplicate Transactions
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Duplicate receipts in tm_transaction_master (same student/class/amount/date/for). Includes
                      full same-day receipts anytime, near double-submits, and every multi-txn Fee Payment
                      duplicate. One receipt with many fee lines still counts as one Transaction row (Fee Payment
                      may list more rows). Fix clears tm_fee_payment, tm_fine_student, tm_transaction_master.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField
                      size="small"
                      label="School code"
                      placeholder="All schools"
                      value={schoolFilter}
                      onChange={(event) => setSchoolFilter(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          applySchoolFilter(event.currentTarget.value);
                        }
                      }}
                      disabled={loadingTxn || !!fixingRowKey}
                      sx={{ minWidth: 200, maxWidth: 260 }}
                      InputProps={{
                        startAdornment: (
                          <FilterIcon sx={{ fontSize: 18, color: active.color, mr: 0.75 }} />
                        ),
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => applySchoolFilter()}
                      disabled={loadingTxn || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        bgcolor: active.color,
                        "&:hover": { bgcolor: crmColors.primary.dark },
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSchoolFilter("");
                        setAppliedSchoolCode("");
                      }}
                      disabled={loadingTxn || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        borderColor: alpha(active.color, 0.4),
                        color: active.color,
                      }}
                    >
                      All schools
                    </Button>
                    {!loadingTxn && !txnError && (
                      <Chip
                        size="small"
                        label={`${txnDuplicates.length} duplicate${txnDuplicates.length === 1 ? "" : "s"}`}
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(active.color, 0.12),
                          color: active.color,
                        }}
                      />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={loadingTxn ? <CircularProgress size={14} /> : <RefreshIcon />}
                      onClick={() => loadTxnDuplicates()}
                      disabled={loadingTxn || !!fixingRowKey}
                      sx={{
                        textTransform: "none",
                        borderColor: alpha(active.color, 0.4),
                        color: active.color,
                      }}
                    >
                      Refresh
                    </Button>
                  </Stack>
                </Box>

                {loadingTxn && txnDuplicates.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress size={28} sx={{ color: active.color }} />
                  </Box>
                ) : txnError && txnDuplicates.length === 0 ? (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="error">
                      {txnError}
                    </Typography>
                  </Box>
                ) : txnDuplicates.length === 0 ? (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {appliedSchoolCode
                        ? `No duplicate transactions found for school code ${appliedSchoolCode}.`
                        : "No duplicate transactions found."}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {txnError ? (
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderBottom: `1px solid ${alpha(crmColors.warning.main, 0.2)}`,
                          bgcolor: alpha(crmColors.warning.main, 0.08),
                        }}
                      >
                        <Typography variant="body2" color="error">
                          {txnError}
                        </Typography>
                      </Box>
                    ) : null}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 56 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8" }}>School</TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8" }}>Student</TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8" }}>Class</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8" }}>
                              Amount
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8" }}>For</TableCell>
                            <TableCell sx={{ fontWeight: 700, bgcolor: "#f7faf8" }}>Date</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8" }}>
                              Txns
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f7faf8", width: 220 }}>
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {txnDuplicates.map((row, index) => {
                            const rowKey = getTxnRowKey(row);
                            const isFixing = fixingRowKey === rowKey;
                            return (
                              <TableRow key={rowKey} hover sx={{ verticalAlign: "middle" }}>
                                <TableCell sx={{ color: "text.secondary" }}>{index + 1}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={row.school_code || "—"}
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: alpha(active.color, 0.12),
                                      color: active.color,
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{row.student_id || "—"}</TableCell>
                                <TableCell>
                                  {row.class_id}/{row.section_id}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                  {row.paid_amount || "—"}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={row.transaction_for || "FEE"}
                                    sx={{ fontWeight: 700 }}
                                  />
                                </TableCell>
                                <TableCell>{row.transaction_date || "—"}</TableCell>
                                <TableCell align="right">
                                  <Chip
                                    size="small"
                                    label={row.transaction_count || row.total}
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: alpha(crmColors.error.main, 0.1),
                                      color: crmColors.error.main,
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<DetailsIcon sx={{ fontSize: 16 }} />}
                                      onClick={() => openTxnDuplicateDetails(row)}
                                      disabled={isFixing}
                                      sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}
                                    >
                                      Details
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={
                                        isFixing ? (
                                          <CircularProgress size={14} color="inherit" />
                                        ) : (
                                          <FixIcon sx={{ fontSize: 16 }} />
                                        )
                                      }
                                      onClick={() => fixTxnRowDuplicate(row)}
                                      disabled={isFixing || !!fixingRowKey || row.can_fix === false}
                                      sx={{
                                        textTransform: "none",
                                        minWidth: 0,
                                        px: 1.25,
                                        bgcolor: active.color,
                                        "&:hover": { bgcolor: crmColors.primary.dark },
                                      }}
                                    >
                                      Fix
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}

            {active &&
              selectedOption !== "student-data" &&
              selectedOption !== "transport" &&
              selectedOption !== "fee-payment" &&
              selectedOption !== "transaction-duplicate" && (
              <Box
                sx={{
                  mt: 2,
                  px: 2,
                  py: 1.75,
                  borderRadius: 2,
                  border: `1px dashed ${alpha(active.color, 0.35)}`,
                  bgcolor: alpha(active.color, 0.05),
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: active.color, mb: 0.5 }}>
                  {active.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duplicate check for {active.label.toLowerCase()} is not wired yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={detailsOpen}
        onClose={closeDuplicateDetails}
        maxWidth={
          detailsType === "fee-payment" || detailsType === "transaction-duplicate" ? "md" : "sm"
        }
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {detailsType === "transport"
            ? "Duplicate Transport Details"
            : detailsType === "fee-payment"
              ? "Duplicate Fee Payment Details"
              : detailsType === "transaction-duplicate"
                ? "Duplicate Transaction Details"
                : "Duplicate Enrollment Details"}
          <IconButton
            onClick={closeDuplicateDetails}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailsRow ? (
            <Stack spacing={1.25} sx={{ mb: 1.5 }}>
              <Typography variant="body2">
                <strong>School:</strong> {detailsData?.school_name || detailsRow.school_code}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">
                  <strong>Code:</strong> {detailsRow.school_code}
                </Typography>
                <SchoolStatusChip status={detailsData?.school_status} />
              </Stack>
              {detailsType === "transport" ? (
                <>
                  <Typography variant="body2">
                    <strong>Student:</strong> {detailsRow.student_id}
                    {detailsData?.student_name ? ` · ${detailsData.student_name}` : ""}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Session:</strong> {detailsRow.session_id}
                  </Typography>
                </>
              ) : detailsType === "fee-payment" ? (
                <>
                  <Typography variant="body2">
                    <strong>Student:</strong> {detailsRow.student_id}
                    {detailsData?.student_name ? ` · ${detailsData.student_name}` : ""}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Session:</strong> {detailsRow.session_id} · <strong>Class:</strong>{" "}
                    {detailsRow.class_id}/{detailsRow.section_id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fee:</strong> {detailsRow.fee_id} · <strong>Month:</strong> {detailsRow.year_month}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Payable:</strong> {detailsRow.fee_payable} · <strong>Paid:</strong> {detailsRow.fee_paid}
                  </Typography>
                  {detailsData ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={
                          detailsData.match_type === "identical"
                            ? "Identical (fully paid)"
                            : detailsData.match_type === "likely_double_submit"
                              ? "Likely double-submit (partial)"
                              : detailsData.match_type === "likely_partial_payments"
                                ? "Likely valid partial payments"
                                : "No duplicate copies"
                        }
                        color={
                          detailsData.match_type === "identical"
                            ? "error"
                            : detailsData.match_type === "likely_double_submit"
                              ? "warning"
                              : detailsData.match_type === "likely_partial_payments"
                                ? "info"
                                : "default"
                        }
                        sx={{ fontWeight: 700 }}
                      />
                      {detailsData.span_minutes !== undefined ? (
                        <Typography variant="caption" color="text.secondary">
                          Time span: {detailsData.span_minutes} min
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : null}
                </>
              ) : detailsType === "transaction-duplicate" ? (
                <>
                  <Typography variant="body2">
                    <strong>Student:</strong> {detailsRow.student_id}
                    {detailsData?.student_name ? ` · ${detailsData.student_name}` : ""}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Session:</strong> {detailsRow.session_id} · <strong>Class:</strong>{" "}
                    {detailsRow.class_id}/{detailsRow.section_id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {detailsRow.paid_amount} · <strong>Date:</strong>{" "}
                    {detailsRow.transaction_date} · <strong>For:</strong>{" "}
                    {detailsRow.transaction_for || detailsData?.transaction_for || "FEE"}
                  </Typography>
                  {detailsData ? (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={
                          detailsData.fee_lines_identical
                            ? "Fee lines identical"
                            : "Fee lines differ"
                        }
                        color={detailsData.fee_lines_identical ? "error" : "warning"}
                        sx={{ fontWeight: 700 }}
                      />
                      <Chip
                        size="small"
                        label={
                          detailsData.can_fix ? "Can fix" : "Review only"
                        }
                        color={detailsData.can_fix ? "success" : "default"}
                        sx={{ fontWeight: 700 }}
                      />
                      {detailsData.span_minutes !== undefined ? (
                        <Typography variant="caption" color="text.secondary">
                          Time span: {detailsData.span_minutes} min
                        </Typography>
                      ) : null}
                      {detailsData.keep_transaction_id ? (
                        <Typography variant="caption" color="text.secondary">
                          Keep: {detailsData.keep_transaction_id}
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : null}
                </>
              ) : (
                <Typography variant="body2">
                  <strong>Enrollment No:</strong> {detailsRow.student_enrollment_no}
                </Typography>
              )}
            </Stack>
          ) : null}

          {detailsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: BRAND }} />
            </Box>
          ) : detailsError &&
            !(detailsData && (detailsType === "fee-payment" || detailsType === "transaction-duplicate")) ? (
            <Typography variant="body2" color="error">
              {detailsError}
            </Typography>
          ) : detailsData && detailsType === "fee-payment" ? (
            <Stack spacing={2}>
              {detailsError ? (
                <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                  {detailsError}
                </Typography>
              ) : null}
              {(detailsData.payment_rows || []).map((row) => (
                <Box
                  key={row.id}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(row.is_keeper ? crmColors.success.main : crmColors.warning.main, 0.28)}`,
                    bgcolor: alpha(row.is_keeper ? crmColors.success.main : crmColors.warning.main, 0.05),
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Payment #{row.id}
                    </Typography>
                    <Chip
                      size="small"
                      label={row.is_keeper ? "Keep" : "Duplicate"}
                      color={row.is_keeper ? "success" : "warning"}
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {row.transaction_id || "—"} · receipt {row.reciept_no || "—"}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Fee {row.fee_id} · {row.year_month} · payable {row.fee_payable} · paid {row.fee_paid}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Txn amount {row.transaction_amount || "—"} · {row.payment_mode || "—"} ·{" "}
                    {row.transaction_date || "—"} · added {row.date_added || "—"}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : detailsData && detailsType === "transaction-duplicate" ? (
            <Stack spacing={2}>
              {detailsError ? (
                <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                  {detailsError}
                </Typography>
              ) : null}
              {(detailsData.transactions || []).map((txn) => (
                <Box
                  key={`${txn.id}-${txn.transaction_id}`}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(
                      txn.is_keeper ? crmColors.success.main : crmColors.warning.main,
                      0.28
                    )}`,
                    bgcolor: alpha(
                      txn.is_keeper ? crmColors.success.main : crmColors.warning.main,
                      0.05
                    ),
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {txn.transaction_id || "—"}
                    </Typography>
                    <Chip
                      size="small"
                      label={txn.is_keeper ? "Keep" : "Clear"}
                      color={txn.is_keeper ? "success" : "warning"}
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      master #{txn.id} · receipt {txn.reciept_no || "—"}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Amount {txn.paid_amount} · {txn.payment_mode || "—"} · for{" "}
                    {txn.transaction_for || "FEE"} · date {txn.transaction_date || "—"} · added{" "}
                    {txn.date_added || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Fee lines: {(txn.fee_rows || []).length}
                    {(txn.fee_rows || []).map((f) => (
                      <span key={f.id}>
                        {" "}
                        · {f.fee_id}/{f.year_month} paid {f.fee_paid}
                      </span>
                    ))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Fine lines: {(txn.fine_rows || []).length}
                    {(txn.fine_rows || []).map((f) => (
                      <span key={f.id}>
                        {" "}
                        · {f.fee_id}/{f.fine_month} paid {f.paid_amount}
                      </span>
                    ))}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : detailsData && detailsType === "transport" ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Transport Rows ({detailsData.transport_rows?.length || 0})
                </Typography>
                {(detailsData.transport_rows || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No transport assignment rows found.
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {detailsData.transport_rows.map((row) => (
                      <Typography key={row.id} variant="body2" color="text.secondary">
                        #{row.id} · route {row.route_id || "—"} · vehicle {row.vehicle_id || "—"} · {row.status || "—"}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Service Rows ({detailsData.service_rows?.length || 0})
                </Typography>
                {(detailsData.service_rows || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No tm_student_services rows found.
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {detailsData.service_rows.map((row) => (
                      <Typography key={row.id} variant="body2" color="text.secondary">
                        #{row.id} · {row.class_id || "—"}/{row.section_id || "—"} · {row.status || "—"}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Duplicate Fee Months
                </Typography>
                {(detailsData.fee_duplicates || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No duplicate fee months found.
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {detailsData.fee_duplicates.map((group) => (
                      <Box key={group.applicable_month}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {group.applicable_month} ({group.total} rows)
                        </Typography>
                        <Stack spacing={0.5}>
                          {(group.rows || []).map((row) => (
                            <Typography key={row.id} variant="caption" color="text.secondary" display="block">
                              #{row.id} · fee {row.net_service_fee || row.service_fee || "—"} · {row.status || "—"}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          ) : detailsData ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Student Names
                </Typography>
                {(detailsData.stu_names || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No student names found.
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {detailsData.stu_names.map((name) => (
                      <Chip
                        key={name}
                        size="small"
                        label={name}
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(BRAND, 0.08),
                          color: crmColors.text.primary,
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Class Details
                </Typography>
                {(detailsData.classes || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No class records found.
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {detailsData.classes.map((cls, clsIndex) => (
                      <Chip
                        key={`${cls.school_code}-${cls.class_label}-${clsIndex}`}
                        size="small"
                        label={cls.class_label}
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(crmColors.info.main, 0.1),
                          color: crmColors.info.dark,
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              {(detailsData.admissions || []).length > 0 ? (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Admission Rows ({detailsData.admissions.length})
                    </Typography>
                    <Stack spacing={0.75}>
                      {detailsData.admissions.map((admission) => (
                        <Typography key={admission.id} variant="body2" color="text.secondary">
                          #{admission.id} · {admission.stu_name || "—"} · {admission.student_code || "—"}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                </>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          {detailsRow &&
          (detailsType === "fee-payment"
            ? !detailsLoading &&
              detailsData &&
              (detailsData.payment_rows?.length || 0) > 1 &&
              (detailsData.can_fix === true ||
                detailsRow.can_fix === true ||
                detailsData.match_type === "identical" ||
                detailsData.match_type === "likely_double_submit")
            : detailsType === "transaction-duplicate"
              ? !detailsLoading &&
                detailsData &&
                (detailsData.transactions?.length || 0) > 1 &&
                (detailsData.can_fix === true ||
                  detailsRow.can_fix === true ||
                  detailsData.fee_lines_identical === true)
              : true) ? (
            <Button
              variant="contained"
              startIcon={
                detailsType === "fee-payment" || detailsType === "transaction-duplicate"
                  ? fixingRowKey
                    ? <CircularProgress size={14} color="inherit" />
                    : <FixIcon />
                  : fixingRowKey ===
                      (detailsType === "transport"
                        ? getTransportRowKey(detailsRow)
                        : getRowKey(detailsRow))
                    ? <CircularProgress size={14} color="inherit" />
                    : <FixIcon />
              }
              onClick={() => {
                if (detailsType === "transport") {
                  fixTransportRowDuplicate(detailsRow);
                } else if (detailsType === "fee-payment") {
                  fixFeeRowDuplicate({
                    school_code: detailsData?.school_code || detailsRow.school_code,
                    session_id: detailsData?.session_id || detailsRow.session_id,
                    student_id: detailsData?.student_id || detailsRow.student_id,
                    class_id:
                      detailsData?.class_id !== undefined && detailsData?.class_id !== null
                        ? detailsData.class_id
                        : detailsRow.class_id,
                    section_id:
                      detailsData?.section_id !== undefined && detailsData?.section_id !== null
                        ? detailsData.section_id
                        : detailsRow.section_id,
                    fee_id: detailsData?.fee_id || detailsRow.fee_id,
                    year_month: detailsData?.year_month || detailsRow.year_month,
                    fee_payable:
                      detailsData?.fee_payable !== undefined && detailsData?.fee_payable !== null
                        ? detailsData.fee_payable
                        : detailsRow.fee_payable,
                    fee_paid:
                      detailsData?.fee_paid !== undefined && detailsData?.fee_paid !== null
                        ? detailsData.fee_paid
                        : detailsRow.fee_paid,
                    match_type: detailsData?.match_type || detailsRow.match_type,
                    can_fix: true,
                  });
                } else if (detailsType === "transaction-duplicate") {
                  fixTxnRowDuplicate({
                    school_code: detailsData?.school_code || detailsRow.school_code,
                    session_id: detailsData?.session_id || detailsRow.session_id,
                    student_id: detailsData?.student_id || detailsRow.student_id,
                    class_id:
                      detailsData?.class_id !== undefined && detailsData?.class_id !== null
                        ? detailsData.class_id
                        : detailsRow.class_id,
                    section_id:
                      detailsData?.section_id !== undefined && detailsData?.section_id !== null
                        ? detailsData.section_id
                        : detailsRow.section_id,
                    paid_amount:
                      detailsData?.paid_amount !== undefined && detailsData?.paid_amount !== null
                        ? detailsData.paid_amount
                        : detailsRow.paid_amount,
                    transaction_date:
                      detailsData?.transaction_date || detailsRow.transaction_date,
                    transaction_for:
                      detailsData?.transaction_for || detailsRow.transaction_for || "FEE",
                    can_fix: true,
                  });
                } else {
                  fixRowDuplicate(detailsRow);
                }
              }}
              disabled={
                !!fixingRowKey ||
                ((detailsType === "fee-payment" || detailsType === "transaction-duplicate") &&
                  detailsLoading)
              }
              sx={{
                textTransform: "none",
                bgcolor: BRAND,
                "&:hover": { bgcolor: crmColors.primary.dark },
              }}
            >
              Fix
            </Button>
          ) : null}
          <Button onClick={closeDuplicateDetails} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </PageContentLayout>
  );
};

export default Validation;
